import { create } from "zustand";
import { api } from "../api/axiosClient";
import {
  VISIBLE_SCAN_SIZE,
  hidesSomeNotifications,
  isNotificationVisible,
} from "../utils/notificationVisibility";

export type NotificationType = "success" | "error" | "warning" | "info";

export interface Notification {
  id: string;
  title: string;
  message: string;
  isRead: boolean;
  creationDate: string;
  deepLinkUrl?: string;
  notificationType?: number;
  isUrgent?: boolean;
}

interface NotificationState {
  open: boolean;
  notifications: Notification[];
  loading: boolean;
  unreadCount: number;
  loadingCount: boolean;
  pageIndex: number;
  pageSize: number;
  hasMore: boolean;

  toggle: () => void;
  addNotification: (notification: Notification) => void;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;

  fetchNotifications: (reset?: boolean) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  open: false,
  notifications: [],
  loading: false,
  unreadCount: 0,
  loadingCount: false,
  pageIndex: 0,
  pageSize: 10,
  hasMore: true,

  toggle: () => set((s) => ({ open: !s.open })),

  addNotification: (notification) => {
    // Nothing about a module the isEmeis switch hides (utils/notificationVisibility).
    if (!isNotificationVisible(notification)) return;
    set((s) => ({
      notifications: [notification, ...s.notifications],
      unreadCount: !notification.isRead ? s.unreadCount + 1 : s.unreadCount,
    }));
  },

  markRead: async (id) => {
    const previousNotifications = get().notifications;
    const previousCount = get().unreadCount;
    const wasUnread =
      previousNotifications.find((n) => n.id === id)?.isRead === false;

    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.id === id ? { ...n, isRead: true } : n,
      ),
      unreadCount:
        wasUnread && s.unreadCount > 0 ? s.unreadCount - 1 : s.unreadCount,
    }));

    try {
      await api.post("/notification/markRead", { id: id });
    } catch (error) {
      console.error("❌ api.post markRead Error:", error);

      set({
        notifications: previousNotifications,
        unreadCount: previousCount,
      });
    }
  },

  markAllRead: async () => {
    const previousNotifications = get().notifications;
    const previousCount = get().unreadCount;

    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    }));

    try {
      await api.post("/notification/markallread", {});
    } catch (error) {
      console.error("❌ api.post markAllRead Error:", error);

      set({
        notifications: previousNotifications,
        unreadCount: previousCount,
      });

      // Rolled back, so the caller must hear about it: swallowing this left
      // both callers' catch blocks dead, and a server failure still showed
      // "All notifications marked as read" over a list that had just been
      // restored to unread.
      throw error;
    }
  },

  fetchNotifications: async (reset = false) => {
    const { pageIndex, pageSize, notifications, loading, hasMore } = get();

    if (loading || (!hasMore && !reset)) return;

    set({ loading: true });
    const currentPage = reset ? 0 : pageIndex;

    try {
      // Pages hidden entirely by the isEmeis switch are skipped, so the panel
      // never shows an empty page while older visible notices exist.
      let page = currentPage;
      let newData: Notification[] = [];
      let rawLength = 0;
      let unreadFromServer: number | null = null;
      for (let tries = 0; tries < 10; tries++) {
        const response = await api.post("/notification/search", {
          pageIndex: page,
          pageSize: pageSize,
          ids: [],
          isRead: null,
          // Newest by creation date — the API default sorts by modification
          // date, which resurfaces old notices once they are marked read.
          orderBy: "newest",
          sortDecending: true,
        });

        const result = response.data;
        const raw: Notification[] = result.data || [];
        rawLength = raw.length;
        newData = [...newData, ...raw.filter(isNotificationVisible)];
        // The badge count comes from /notification/getCount and covers every
        // unread notice. Recomputing it from the page just loaded shrank it to
        // at most one page (24 became 10 on opening the panel), so only take a
        // count the search itself reports — and only when nothing is hidden,
        // since the server's count includes hidden notices.
        if (typeof result.unreadCount === "number" && !hidesSomeNotifications())
          unreadFromServer = result.unreadCount;
        page += 1;
        if (newData.length > 0 || rawLength < pageSize) break;
      }

      const updatedNotifications = reset
        ? newData
        : [...notifications, ...newData];

      set({
        notifications: updatedNotifications,
        ...(unreadFromServer != null ? { unreadCount: unreadFromServer } : {}),
        pageIndex: page,
        hasMore: rawLength === pageSize,
        loading: false,
      });
    } catch (error) {
      console.error("❌ api.post fetchNotifications Error:", error);
      set({ loading: false });
    }
  },

  fetchUnreadCount: async () => {
    set({ loadingCount: true });
    try {
      let count: number;
      if (hidesSomeNotifications()) {
        // The server counts every unread notice, including those the isEmeis
        // switch hides, so count the visible unread ones here.
        const response = await api.post("/notification/search", {
          pageIndex: 0,
          pageSize: VISIBLE_SCAN_SIZE,
          ids: [],
          isRead: false,
          orderBy: "newest",
          sortDecending: true,
        });
        const unread: Notification[] = response.data?.data || [];
        count = unread.filter(isNotificationVisible).length;
      } else {
        const response = await api.post("/notification/getCount", {});
        const result = response.data;
        count =
          typeof result === "number"
            ? result
            : result.data || result.count || 0;
      }

      set({
        unreadCount: count,
        loadingCount: false,
      });
    } catch (error) {
      console.error("❌ api.post fetchUnreadCount Error:", error);
      set({ loadingCount: false });
    }
  },
}));
